import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Receipt, Wallet, CheckCircle } from '@phosphor-icons/react';

const fmt = (n) => `\u20B9${(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Receive Payment dialog — simplified flow.
 *
 * One popup, one save button:
 *   1. Bill summary at the top (customer, invoice number, total, live due).
 *   2. Available-credit banner with a single `Don't use credit` toggle
 *      (default: credit is auto-applied to the maximum extent possible).
 *   3. Cash Received field — auto-fills to `due - applied_credit`. Editable.
 *      Any amount above the remaining due is parked as customer advance
 *      (no overpay confirmation needed — already handled by the backend).
 *   4. Payment Mode + Date.
 *   5. Single Save action.
 *
 * In edit-mode (a `receipt` prop is provided) the dialog falls back to a
 * compact "amend this receipt" view — the credit/advance machinery is
 * irrelevant because the receipt is already booked.
 *
 * Required props:
 *   open / onOpenChange / invoice / accounts / onSaved
 *   receipt — pass `null` to create, full receipt object to edit
 */
export default function PaymentReceiptDialog({ open, onOpenChange, invoice, receipt, accounts, onSaved }) {
  const isCreate = !receipt;
  const todayIso = new Date().toISOString().split('T')[0];

  // Live invoice snapshot is refreshed after any state change so the Due
  // value displayed stays in lockstep with the bill on disk.
  const [liveInvoice, setLiveInvoice] = useState(invoice);
  const [creditBalance, setCreditBalance] = useState(0);
  const [skipCredit, setSkipCredit] = useState(false);
  const [cashStr, setCashStr] = useState('');
  const [mode, setMode] = useState('');
  const [payDate, setPayDate] = useState(todayIso);
  const [saving, setSaving] = useState(false);
  // Other unpaid bills of this customer that the staff can optionally roll
  // into the same receipt. `oldDues` is the raw fetched list; `selectedOld`
  // is a {invoice_id -> amount-string} map driven by checkbox + per-row input.
  const [oldDues, setOldDues] = useState([]);
  const [selectedOld, setSelectedOld] = useState({});

  useEffect(() => { setLiveInvoice(invoice); }, [invoice]);

  // Reset on each open so the dialog never leaks state across customers.
  // Default `skipCredit=true` so the cash field defaults to the FULL bill due
  // (no implicit "Apply credit" debit). Staff can still tick the toggle to
  // apply available credit before saving.
  useEffect(() => {
    if (!open) return;
    setSkipCredit(true);
    // Sync liveInvoice IMMEDIATELY (don't wait for the [invoice]-mirror
    // useEffect) so the auto-sync useEffect below computes `cashSuggested`
    // from the right bill on the first render after `open` flips true.
    // Without this, the cash field used to display the previous invoice's
    // due (or '0') on the very first paint.
    setLiveInvoice(invoice);
    if (isCreate) {
      setPayDate(todayIso);
      const defaultMode = accounts?.find(a => a.type === 'cash') || accounts?.find(a => a.type === 'bank');
      setMode(defaultMode?.id || '');
      // Pre-fill cash with the bill's remaining due so the staff just confirms
      // and saves for the common "customer paid the exact amount" flow.
      const dueNow = Math.max(0, (invoice?.total || 0) - (invoice?.amount_paid || 0));
      setCashStr(dueNow > 0 ? String(dueNow) : '');
    } else {
      setCashStr(String(receipt.amount ?? ''));
      setMode(receipt.payment_mode || '');
      setPayDate((receipt.payment_date || receipt.date || todayIso).slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.id, receipt]);

  // Pull live customer credit balance whenever the popup opens (create-mode).
  useEffect(() => {
    if (!open || !isCreate || !liveInvoice?.customer_id) return;
    api.get(`/customers/${liveInvoice.customer_id}`)
      .then(r => setCreditBalance(r.data?.advance_balance || 0))
      .catch(() => setCreditBalance(0));
  }, [open, isCreate, liveInvoice?.customer_id]);

  // Pull this customer's OTHER unpaid / partial invoices so the staff can
  // optionally roll them into this single receipt — saves running back to
  // the Customers page when a walk-in pays off multiple pending bills at once.
  useEffect(() => {
    if (!open || !isCreate || !liveInvoice?.customer_id) return;
    setSelectedOld({});
    api.get('/invoices')
      .then(r => {
        const list = (r.data || [])
          .filter(inv => inv.customer_id === liveInvoice.customer_id
                          && inv.id !== liveInvoice.id
                          && (inv.balance_due || 0) > 0.01);
        setOldDues(list);
      })
      .catch(() => setOldDues([]));
  }, [open, isCreate, liveInvoice?.customer_id, liveInvoice?.id]);

  // ----- derived view -----
  const paidBefore = liveInvoice?.amount_paid || 0;
  const due = isCreate ? Math.max(0, (liveInvoice?.total || 0) - paidBefore) : (receipt.amount || 0);
  // Credit ALREADY adjusted to this bill from earlier apply-advance / unadjusted
  // receipt clicks. Surfaced in the "From credit" line so the staff sees the
  // running total of customer credit absorbed by this invoice.
  const alreadyAdjusted = isCreate
    ? (liveInvoice?.payment_history || [])
        .filter(p => p?.from_advance)
        .reduce((s, p) => s + (p?.amount || 0), 0)
    : 0;
  // Credit about to be applied THIS session — only when the user opts in via
  // the "Use credit" toggle (default off so cash field defaults to the due).
  const creditApplied = isCreate && !skipCredit ? Math.min(creditBalance, due) : 0;
  const cashSuggested = Math.max(0, due - creditApplied);

  // The cash field auto-syncs with `cashSuggested` while the user hasn't
  // typed anything. Once they type, we respect their value (controlled).
  const [cashTouched, setCashTouched] = useState(false);
  useEffect(() => { setCashTouched(false); }, [open, invoice?.id]);
  useEffect(() => {
    if (!cashTouched && isCreate) setCashStr(cashSuggested > 0 ? String(cashSuggested.toFixed(2)) : '0');
  }, [cashSuggested, cashTouched, isCreate]);

  const cash = parseFloat(cashStr) || 0;
  const overpay = isCreate ? Math.max(0, cash - (due - creditApplied)) : 0;
  // Sum of amounts the staff has earmarked for old dues. Each row is clamped
  // to its own balance on save; we trust the input here for the live preview.
  const oldDuesApplyTotal = Object.entries(selectedOld).reduce((s, [, v]) => s + (parseFloat(v) || 0), 0);
  // Old Due Total (gross outstanding across all OTHER unpaid bills of this
  // customer) — shown separately even when nothing is selected so the staff
  // knows the customer's total pending burden at a glance.
  const oldDueTotal = oldDues.reduce((s, inv) => s + (inv.balance_due || 0), 0);
  const finalStatus = (() => {
    if (!isCreate) return null;
    const totalApplied = cash + creditApplied;
    if (totalApplied >= due - 0.01) return 'paid';
    if (totalApplied > 0) return 'partial';
    return 'unchanged';
  })();

  // ----- save -----
  const save = async () => {
    if (payDate > todayIso) return toast.error('Payment date cannot be in the future');

    if (!isCreate) {
      // Amend existing receipt — no credit logic, just the basic PUT.
      if (cash <= 0) return toast.error('Amount must be greater than zero');
      if (!mode) return toast.error('Payment mode is required');
      setSaving(true);
      try {
        await api.put(`/invoices/${liveInvoice.id}/receipts/${receipt.receipt_number}`, {
          amount: cash, payment_mode: mode, payment_date: payDate || null,
        });
        toast.success(`${receipt.receipt_number} updated`);
        onSaved?.();
        onOpenChange?.(false);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to save receipt');
      } finally { setSaving(false); }
      return;
    }

    if (creditApplied <= 0 && cash <= 0 && oldDuesApplyTotal <= 0) {
      return toast.error('Nothing to receive — type a cash amount, tick an old bill, or enable credit');
    }
    if ((cash > 0 || oldDuesApplyTotal > 0) && !mode) return toast.error('Payment mode is required for cash');

    setSaving(true);
    try {
      let parts = [];
      // 1) Apply credit first — keeps the bill cleanly settled and any
      //    fresh cash receipt then targets the remaining balance.
      if (creditApplied > 0) {
        const r = await api.post(`/invoices/${liveInvoice.id}/apply-advance`, { amount: creditApplied });
        parts.push(`${fmt(creditApplied)} from credit (${r.data?.receipt_number || ''})`);
      }
      // 2) Cash receipt against THIS bill — backend auto-parks any excess
      //    beyond the remaining due as a fresh advance for the same customer.
      if (cash > 0) {
        const r = await api.put(`/invoices/${liveInvoice.id}/payment`, {
          amount: cash, payment_mode: mode, payment_date: payDate || null,
        });
        const tag = (r.data?.overpay_amount || 0) > 0 ? ` (₹${r.data.overpay_amount.toFixed(2)} parked as credit)` : '';
        parts.push(`${fmt(cash)} cash (${r.data?.receipt_number || ''})${tag}`);
      }
      // 3) Selected OLD-DUE bills — each one gets its own receipt voucher
      //    against the same payment mode + date. Amount per bill is clamped
      //    to its remaining balance so excess doesn't accidentally park as
      //    credit on a different invoice.
      for (const oldInv of oldDues) {
        const raw = parseFloat(selectedOld[oldInv.id]);
        if (!raw || raw <= 0) continue;
        const applyAmt = Math.min(raw, oldInv.balance_due || 0);
        if (applyAmt <= 0) continue;
        const r = await api.put(`/invoices/${oldInv.id}/payment`, {
          amount: applyAmt, payment_mode: mode, payment_date: payDate || null,
        });
        parts.push(`${fmt(applyAmt)} → ${oldInv.invoice_number} (${r.data?.receipt_number || ''})`);
      }
      toast.success(`Payment recorded — ${parts.join(' + ')}`);
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save receipt');
    } finally { setSaving(false); }
  };

  // Read-only popup when the bill is already fully paid in create-mode.
  const fullyPaid = isCreate && due <= 0.01;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[90vh] p-0 rounded-xl border border-[#E4E4E7] flex flex-col overflow-hidden"
        data-testid="receipt-dialog"
      >
        <DialogHeader className="px-6 pt-4 pb-3 border-b border-[#E4E4E7] bg-[#FAFAFA] shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Receipt size={20} weight="duotone" className="text-[#10B981]" />
            {isCreate ? 'Receive Payment' : `Amend ${receipt?.receipt_number}`}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#52525B]">
            {isCreate
              ? 'Combine available credit + cash in one go. Tick old bills to clear multiple invoices in a single receipt.'
              : 'Edit the amount / mode / date of this booked receipt.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {fullyPaid ? (
            <div className="max-w-md mx-auto rounded-xl border border-[#10B981] bg-[#ECFDF5] p-6 text-center" data-testid="receipt-fully-paid">
              <CheckCircle size={36} weight="duotone" className="text-[#10B981] mx-auto" />
              <p className="text-base font-semibold text-[#065F46] mt-2">Invoice fully paid</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6 max-w-7xl mx-auto">
              {/* LEFT — bill summary + cash inputs + breakdown */}
              <div className="space-y-5">
                {/* Bill summary */}
                <div className="grid grid-cols-2 gap-3 rounded-xl bg-[#F4F4F5] p-4" data-testid="receipt-snapshot">
                  <div>
                    <p className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Customer</p>
                    <p className="text-sm font-semibold text-[#0A0A0A]" data-testid="receipt-customer-name">{liveInvoice?.customer_name || '—'}</p>
                    <p className="text-[10px] text-[#A1A1AA] font-mono mt-0.5">{liveInvoice?.invoice_number || '—'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Bill Total</p>
                    <p className="text-sm font-bold font-mono">{fmt(liveInvoice?.total || 0)}</p>
                    <p className={`text-base font-bold font-mono ${due > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`} data-testid="receipt-due-amount">
                      Due {fmt(due)}
                    </p>
                  </div>
                </div>

                {/* Credit banner */}
                {isCreate && creditBalance > 0 && (
                  <div className="rounded-xl border border-[#3B82F6]/40 bg-[#EFF6FF] p-4" data-testid="credit-banner">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Wallet size={18} weight="duotone" className="text-[#3B82F6]" />
                        <div>
                          <p className="text-xs font-semibold text-[#1D4ED8]">Customer credit available — {fmt(creditBalance)}</p>
                          <p className="text-[11px] text-[#1E40AF]">
                            {skipCredit
                              ? 'Credit kept aside. Tick to apply toward this bill.'
                              : `Applying ${fmt(creditApplied)} from credit toward this bill.`}
                          </p>
                        </div>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={!skipCredit}
                          onChange={e => setSkipCredit(!e.target.checked)}
                          className="h-4 w-4 rounded border-[#3B82F6] accent-[#3B82F6]"
                          data-testid="use-credit-toggle"
                        />
                        <span className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#1D4ED8]">Use credit</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Cash Received + Mode + Date */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Cash Received</Label>
                    <Input
                      type="number"
                      inputMode="decimal"
                      value={cashStr}
                      onFocus={() => setCashTouched(true)}
                      onChange={e => { setCashTouched(true); setCashStr(e.target.value); }}
                      placeholder="0"
                      className="rounded-lg mt-1 font-mono"
                      data-testid="receipt-amount-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Payment Mode</Label>
                    <Select value={mode} onValueChange={setMode}>
                      <SelectTrigger className="rounded-lg mt-1" data-testid="receipt-mode-select">
                        <SelectValue placeholder="Select mode" />
                      </SelectTrigger>
                      <SelectContent>
                        {(accounts || []).filter(a => {
                          if (['cash', 'bank', 'wallet', 'creditor'].includes(a.type)) return true;
                          if (a.type === 'credit_card') return (a.balance || 0) <= 0;
                          return false;
                        }).map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Date</Label>
                    <Input
                      type="date"
                      value={payDate}
                      max={todayIso}
                      onChange={e => setPayDate(e.target.value)}
                      className="rounded-lg mt-1 font-mono"
                      data-testid="receipt-date-input"
                    />
                  </div>
                </div>

                {/* Live breakdown */}
                {isCreate && (
                  <div className="rounded-lg bg-[#FAFAFA] border border-[#E4E4E7] px-4 py-3 text-[12px] space-y-1.5" data-testid="receipt-breakdown">
                    <div className="flex items-center justify-between" data-testid="from-credit-row">
                      <span className="text-[#52525B]">
                        From credit
                        {alreadyAdjusted > 0 && (
                          <span className="text-[10px] text-[#3B82F6] ml-1">
                            (already adjusted {fmt(alreadyAdjusted)}{creditApplied > 0 ? ` + ${fmt(creditApplied)} now` : ''})
                          </span>
                        )}
                      </span>
                      <span className="font-mono">{fmt(alreadyAdjusted + creditApplied)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#52525B]">Cash received (this bill)</span>
                      <span className="font-mono">{fmt(cash)}</span>
                    </div>
                    {oldDuesApplyTotal > 0 && (
                      <div className="flex items-center justify-between text-[#B91C1C]" data-testid="old-dues-apply-row">
                        <span>Applied to old bills</span>
                        <span className="font-mono font-semibold">{fmt(oldDuesApplyTotal)}</span>
                      </div>
                    )}
                    <div className="border-t border-[#E4E4E7] my-1" />
                    <div className="flex items-center justify-between" data-testid="receipt-total-row">
                      <span className="text-[#0A0A0A] font-semibold">Total received</span>
                      <span className="font-mono font-bold text-[#0A0A0A] text-sm">{fmt(cash + oldDuesApplyTotal)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#0A0A0A] font-semibold">Bill outcome</span>
                      <span className="font-mono font-semibold">
                        {finalStatus === 'paid' && <span className="text-[#10B981]">Paid in full</span>}
                        {finalStatus === 'partial' && <span className="text-[#F59E0B]">Partial ({fmt(due - creditApplied - cash)} remaining)</span>}
                        {finalStatus === 'unchanged' && <span className="text-[#A1A1AA]">No change</span>}
                      </span>
                    </div>
                    {overpay > 0 && (
                      <div className="flex items-center justify-between text-[#1D4ED8]">
                        <span>Excess parked as customer credit</span>
                        <span className="font-mono font-semibold">{fmt(overpay)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* RIGHT — other unpaid bills list (always present in full-page) */}
              {isCreate && (
                <div className="rounded-xl border border-[#FCA5A5]/60 bg-[#FFF7F5] p-4 flex flex-col" data-testid="old-dues-section">
                  <div className="flex items-center justify-between mb-3 shrink-0">
                    <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#B91C1C]">
                      Other unpaid bills · {oldDues.length}
                    </p>
                    <p className="text-[12px] text-[#B91C1C]" data-testid="old-due-total">
                      Old Due Total: <span className="font-mono font-bold">{fmt(oldDueTotal)}</span>
                    </p>
                  </div>
                  {oldDues.length === 0 ? (
                    <p className="text-center text-xs text-[#A1A1AA] italic py-8" data-testid="old-dues-empty">
                      No other unpaid bills for this customer.
                    </p>
                  ) : (
                    <div className="flex-1 overflow-y-auto space-y-1 pr-1 min-h-0">
                      {oldDues.map(o => {
                        const checked = selectedOld[o.id] !== undefined;
                        const balance = o.balance_due || 0;
                        return (
                          <div key={o.id} className="flex items-center gap-2 bg-white rounded-md px-2 py-2 border border-[#FCA5A5]/40" data-testid={`old-due-row-${o.invoice_number}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => {
                                setSelectedOld(prev => {
                                  const next = { ...prev };
                                  if (e.target.checked) next[o.id] = String(balance);
                                  else delete next[o.id];
                                  return next;
                                });
                              }}
                              className="h-3.5 w-3.5 rounded border-[#EF4444] accent-[#EF4444]"
                              data-testid={`old-due-check-${o.invoice_number}`}
                            />
                            <span className="text-[12px] font-mono font-semibold text-[#0A0A0A] flex-1 truncate">{o.invoice_number}</span>
                            <span className="text-[10px] text-[#A1A1AA] font-mono">Bal {fmt(balance)}</span>
                            <Input
                              type="number"
                              inputMode="decimal"
                              value={selectedOld[o.id] ?? ''}
                              placeholder="0"
                              disabled={!checked}
                              onChange={e => setSelectedOld(prev => ({ ...prev, [o.id]: e.target.value }))}
                              className="h-7 w-24 text-[11px] rounded-md font-mono disabled:bg-[#F4F4F5]"
                              data-testid={`old-due-amount-${o.invoice_number}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-[#E4E4E7] bg-[#FAFAFA] shrink-0">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-lg" data-testid="receipt-cancel-btn">
            {fullyPaid ? 'Close' : 'Cancel'}
          </Button>
          {!fullyPaid && (
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#10B981] text-white hover:bg-[#059669] hover:text-white rounded-lg px-6"
              data-testid="receipt-save-btn"
            >
              {saving ? 'Saving…' : isCreate ? 'Save Payment' : 'Update Receipt'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
